// AI-CONTEXT: See docs/features/impact-per-euro-methodiek.md
import { useMemo } from 'react'
import { useFirestore, useFirestoreGeneral } from '../firebase/useFirestore'
import useParticipationParameters from './useParticipationParameters'
import { sumParticipationPeriodValue } from '../helpers/participationHelpers/selectPeriodProfiles'
import useParticipationBirthDates from './useParticipationBirthDates'
import { isPostsModel } from '../helpers/participationHelpers/calculateParticipationImpact'
import { client } from './Client'

/**
 * Boekjaar-gescopete participatieladder-teller (methodiek § 1, cohortprincipe):
 * de som van de periode-delta's uit de `ParticipationMeasurements`-log over
 * één kalenderjaar (D3), plus het aantal gekoppelde metingen voor de
 * ≥ 20-drempel (§ 5.2 / D5).
 *
 * Geeft naast het aantal gekoppelde metingen ook terug hoeveel deelnemers er
 * juist NIET meetellen en waarom (`zonderMetingInPeriode`,
 * `zonderHoofdactiviteit`). De detailpagina toont die uitsplitsing; zonder haar
 * is een te laag totaal niet te onderscheiden van een organisatie die niets
 * gemeten heeft.
 * AI-CONTEXT: See docs/pitfalls.md#dev-gate-counter-hides-its-own-policy
 *
 * @param {number} year - Boekjaar (= kalenderjaar)
 * Sinds de rekenregels van de MSI (docs/features/msi-rekenregels-monetarisering.md)
 * ook `attributedValue`: dezelfde periodewaarde toegerekend aan de interventie,
 * met `notAttributedCount` voor wie geen duur of uren heeft.
 *
 * @returns {{ value: number | null, attributedValue: number | null, notAttributedCount: number | null, notAttributedReasons: Object<string, number>, linkedMeasurementCount: number | null, participation: object | null, loading: boolean, error: Error | null }}
 *   value/count zijn null tot de rekenparameters geladen zijn — nooit een
 *   deelresultaat dat zich als compleet voordoet. `participation` is de
 *   volledige uitkomst van sumParticipationPeriodValue, voor consumers die de
 *   afgevallen deelnemers willen benoemen.
 */
const useParticipationPeriodValue = (year) => {
    const { parameters, loading, error } = useParticipationParameters()
    const personas = useFirestoreGeneral('Personas', 'Status', 'active')
    const measurements = useFirestore('ParticipationMeasurements')
    // Standaard duur en uren van de organisatie (rekenregels MSI); '' zolang de read loopt.
    const compagnyMeta = useFirestoreGeneral('CompagnyMeta', 'CompagnyID', client)
    const doseLoaded = Array.isArray(compagnyMeta)
    const orgDefault = doseLoaded ? (compagnyMeta[0]?.DefaultInterventionDose || null) : null
    const orgDefaultKey = JSON.stringify(orgDefault)
    // Geboortedatums voor de leeftijdsovergang (alleen onder 2026.3).
    const { birthDates, error: birthDatesError } = useParticipationBirthDates(isPostsModel(parameters))

    const result = useMemo(() => {
        const jaar = Number(year)
        if (!parameters || !birthDates || !doseLoaded || !Number.isFinite(jaar)) return null

        // Lokale jaargrenzen, als epoch-ms (zelfde eenheid als de MSI-stroom —
        // AI-CONTEXT: See docs/pitfalls.md#api-callable-timestamp-vs-ms).
        const startMs = new Date(jaar, 0, 1).getTime()
        const endMs = new Date(jaar + 1, 0, 1).getTime() - 1

        return sumParticipationPeriodValue({
            personas: Array.isArray(personas) ? personas : [],
            measurements: Array.isArray(measurements) ? measurements : [],
            startMs,
            endMs,
            parameters,
            birthDates,
            orgDefault,
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [personas, measurements, parameters, birthDates, year, doseLoaded, orgDefaultKey])

    return {
        value: result ? result.value : null,
        attributedValue: result ? result.attributedValue : null,
        notAttributedCount: result ? result.notAttributedCount : null,
        notAttributedReasons: result ? result.notAttributedReasons : {},
        linkedMeasurementCount: result ? result.linkedMeasurementCount : null,
        participation: result,
        loading: loading || !doseLoaded || (Boolean(parameters) && birthDates === null && !birthDatesError),
        error: error || birthDatesError,
    }
}

export default useParticipationPeriodValue
